import React, { useCallback, useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import { Play, Loader2 } from 'lucide-react';
import { classNames } from '@/lib/format';

const THUMB_SEEK_SEC = 0.5;

function getBaseMediaUrl(u) {
  if (!u) return '';
  try {
    const parsed = new URL(u);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return String(u).split('?')[0];
  }
}

/**
 * Session / artifact video player.
 * When `interactive` is true, exposes seek/getCurrentTime via ref for integrity markers.
 */
export const VideoWidget = forwardRef(function VideoWidget(
  {
    url,
    mediaKey,
    poster,
    title,
    duration,
    posterColor = 'from-slate-100 to-slate-200',
    className,
    interactive = false,
    onTimeUpdate,
    autoOpen = false,
    onOpen,
    isOpen,
    onPlayStateChange,
  },
  ref,
) {
  const [internalOpen, setInternalOpen] = useState(!!autoOpen && !!url);
  const isControlled = typeof isOpen === 'boolean';
  const open = isControlled ? isOpen : internalOpen;
  const [hovering, setHovering] = useState(false);
  const videoRef = useRef(null);
  const thumbVideoRef = useRef(null);
  const activeUrlRef = useRef(url);
  const lockedThumbRef = useRef({ key: null, src: null });
  const [thumbReady, setThumbReady] = useState(false);
  const [thumbFailed, setThumbFailed] = useState(false);

  // Keep the active <video> source URL stable if the media file (base URL) hasn't changed.
  // Presigned S3 URLs rotate query string signatures on every polling request, which
  // otherwise causes HTML5 <video> to abort, blink/empty, and restart playback every poll.
  if (url && (!activeUrlRef.current || getBaseMediaUrl(url) !== getBaseMediaUrl(activeUrlRef.current))) {
    activeUrlRef.current = url;
  }

  const stableKey = mediaKey || getBaseMediaUrl(url) || null;
  const thumbSrc = (stableKey && lockedThumbRef.current.key === stableKey && lockedThumbRef.current.src)
    ? lockedThumbRef.current.src
    : (activeUrlRef.current || url);
  const isLoadingThumb = !poster && !!thumbSrc && !thumbReady && !thumbFailed;

  useEffect(() => {
    if (lockedThumbRef.current.key !== stableKey) {
      lockedThumbRef.current = { key: stableKey, src: null };
      setThumbReady(false);
      setThumbFailed(false);
    }
  }, [stableKey]);

  const seekThumbnail = useCallback((video) => {
    if (!video || !video.duration || Number.isNaN(video.duration)) return;
    const t = Math.min(THUMB_SEEK_SEC, Math.max(0, video.duration - 0.1));
    try {
      video.currentTime = t;
    } catch {
      /* ignore seek errors on some browsers */
    }
  }, []);

  const onLoadedMetadata = useCallback(() => {
    const video = thumbVideoRef.current;
    if (!video) return;
    if (stableKey && video.src) {
      lockedThumbRef.current = { key: stableKey, src: video.src };
    }
    seekThumbnail(video);
    video.pause();
    setThumbReady(true);
  }, [seekThumbnail, stableKey]);

  const onMouseEnter = useCallback(() => {
    setHovering(true);
    const video = thumbVideoRef.current;
    if (!video || !thumbSrc || thumbFailed || !thumbReady) return;
    video.muted = true;
    video.currentTime = 0;
    video.play().catch(() => {});
  }, [thumbSrc, thumbFailed, thumbReady]);

  const onMouseLeave = useCallback(() => {
    setHovering(false);
    const video = thumbVideoRef.current;
    if (!video) return;
    video.pause();
    seekThumbnail(video);
  }, [seekThumbnail]);

  const handleClick = useCallback(() => {
    const video = thumbVideoRef.current;
    if (video) {
      video.pause();
      seekThumbnail(video);
    }
    setHovering(false);
    if (onOpen) {
      onOpen();
    } else {
      if (!isControlled) {
        setInternalOpen(true);
      }
      onPlayStateChange?.(true);
    }
  }, [onOpen, isControlled, onPlayStateChange, seekThumbnail]);

  useImperativeHandle(ref, () => ({
    seek(tSec) {
      const el = videoRef.current;
      if (!el) return;
      el.currentTime = Math.max(0, Number(tSec) || 0);
      void el.play?.();
    },
    getCurrentTime() {
      return Math.floor(videoRef.current?.currentTime || 0);
    },
    openPlayer() {
      if (!isControlled) {
        setInternalOpen(true);
      }
      onPlayStateChange?.(true);
    },
  }), [isControlled, onPlayStateChange]);

  useEffect(() => {
    if (!open) {
      setHovering(false);
    }
  }, [open]);

  useEffect(() => {
    if (autoOpen && url) {
      if (!isControlled) setInternalOpen(true);
      onPlayStateChange?.(true);
    }
  }, [autoOpen, url, isControlled, onPlayStateChange]);

  return (
    <div className={classNames('group relative overflow-hidden rounded-xl border bg-card', className)} data-testid="video-widget">
      {!open ? (
        <button
          type="button"
          onClick={handleClick}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          data-testid="video-widget-play-button"
          className="relative block w-full text-left"
        >
          <div className={classNames('relative aspect-video overflow-hidden bg-gradient-to-br', posterColor)}>
            {poster ? (
              <img
                src={poster}
                alt={title || 'Video thumbnail'}
                className="absolute inset-0 h-full w-full object-cover"
              />
            ) : (
              thumbSrc && !thumbFailed && (
                <video
                  ref={thumbVideoRef}
                  src={thumbSrc}
                  preload="metadata"
                  muted
                  playsInline
                  className={classNames(
                    'absolute inset-0 h-full w-full object-cover transition-opacity duration-200',
                    !thumbReady && 'opacity-0',
                  )}
                  onLoadedMetadata={onLoadedMetadata}
                  onError={() => setThumbFailed(true)}
                />
              )
            )}
            <div className="absolute inset-0 grid place-items-center">
              <span
                className={classNames(
                  'grid h-14 w-14 place-items-center rounded-full bg-white shadow-[0_10px_24px_rgba(16,24,40,0.18)] transition-all duration-200',
                  hovering && !isLoadingThumb ? 'opacity-0 scale-90 pointer-events-none' : 'group-hover:scale-105',
                )}
              >
                {isLoadingThumb ? (
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" data-testid="video-thumb-loader" />
                ) : (
                  <Play className="h-6 w-6 translate-x-[1px] text-foreground" />
                )}
              </span>
            </div>
            <div className="absolute bottom-2 right-2 rounded-md bg-black/70 px-1.5 py-0.5 text-[11px] font-medium text-white tabular-nums">{duration || '01:00'}</div>
          </div>
          {title && <div className="p-3 text-sm font-medium">{title}</div>}
        </button>
      ) : (
        <div className="w-full">
          <video
            ref={interactive ? videoRef : undefined}
            controls
            autoPlay
            src={activeUrlRef.current || url}
            className="aspect-video w-full bg-black"
            data-testid="video-player"
            onTimeUpdate={interactive && onTimeUpdate ? (e) => onTimeUpdate(Math.floor(e.currentTarget.currentTime || 0)) : undefined}
            onEnded={() => {
              if (!isControlled) setInternalOpen(false);
              onPlayStateChange?.(false);
            }}
          />
          {title && <div className="p-3 text-sm font-medium">{title}</div>}
        </div>
      )}
    </div>
  );
});
